import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Settings, Edit, Trash2, Plus, Stethoscope } from 'lucide-react';
import EngineerSidebar from '../Sidebar/EngineerSidebar';
import { useToast } from '../../hooks/use-toast';
import { adminConsultingAPI } from '../../api';

const AdminPanel = () => {
  const { toast } = useToast();
  const [services, setServices] = useState([]);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [editingOption, setEditingOption] = useState(null);
  const [newService, setNewService] = useState({
    name: '',
    key: '',
    description: '',
    price: '',
    category: 'tuning'
  });
  const [newOption, setNewOption] = useState({
    name: '',
    key: '',
    description: '',
    price: '',
    category: 'performance',
    compatible_services: []
  });
  const [consultingPrice, setConsultingPrice] = useState(50.0);
  const [editingConsultingPrice, setEditingConsultingPrice] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    await Promise.all([loadServices(), loadOptions(), loadConsultingPrice()]);
    setLoading(false);
  };

  const loadServices = async () => {
    console.log('[LOAD_SERVICES] Starting to load services...');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/services`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      console.log('[LOAD_SERVICES] Received services:', data.length, 'services');
      console.log('[LOAD_SERVICES] Services:', data.map(s => s.name).join(', '));
      setServices(data);
      console.log('[LOAD_SERVICES] State updated successfully');
    } catch (error) {
      console.error('[LOAD_SERVICES] Error loading services:', error);
    }
  };

  const loadOptions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/options`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setOptions(data);
    } catch (error) {
      console.error('Error loading options:', error);
    }
  };

  const loadConsultingPrice = async () => {
    try {
      const data = await adminConsultingAPI.getPrice();
      setConsultingPrice(data.price);
    } catch (error) {
      console.error('Error loading consulting price:', error);
    }
  };

  const handleUpdateConsultingPrice = async () => {
    try {
      await adminConsultingAPI.updatePrice(consultingPrice);
      toast({ title: "Succès", description: "Prix du consulting mis à jour" });
      setEditingConsultingPrice(false);
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur lors de la mise à jour", variant: "destructive" });
    }
  };

  const handleCreateService = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/services`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newService,
          key: newService.key || newService.name.toLowerCase().replace(/\s+/g, '_'),
          price: parseFloat(newService.price)
        })
      });

      if (response.ok) {
        toast({ title: "Succès", description: "Service créé avec succès" });
        setNewService({ name: '', key: '', description: '', price: '', category: 'tuning' });
        loadServices();
      } else {
        const error = await response.json();
        toast({ title: "Erreur", description: error.detail || "Erreur lors de la création", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur lors de la création du service", variant: "destructive" });
    }
  };

  const handleCreateOption = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/options`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newOption,
          key: newOption.key || newOption.name.toLowerCase().replace(/\s+/g, '_'),
          price: parseFloat(newOption.price)
        })
      });

      if (response.ok) {
        toast({ title: "Succès", description: "Option créée avec succès" });
        setNewOption({ name: '', key: '', description: '', price: '', category: 'performance', compatible_services: [] });
        loadOptions();
      } else {
        const error = await response.json();
        toast({ title: "Erreur", description: error.detail || "Erreur lors de la création", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur lors de la création de l'option", variant: "destructive" });
    }
  };

  const handleUpdateService = async (e) => {
    e.preventDefault();
    if (!editingService) return;

    const serviceKey = editingService.key || editingService.id;
    console.log('[UPDATE] Updating service:', serviceKey);
    console.log('[UPDATE] Service data:', editingService);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/services/${serviceKey}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editingService.name,
          description: editingService.description,
          price: parseFloat(editingService.price),
          active: editingService.active
        })
      });

      console.log('[UPDATE] Response status:', response.status);

      if (response.ok) {
        toast({ title: "Succès", description: "Service mis à jour avec succès" });
        setEditingService(null);
        await loadServices();
        console.log('[UPDATE] Services reloaded');
      } else {
        let errorMessage = "Erreur lors de la mise à jour";
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
          console.error('[UPDATE] Error response:', error);
        } catch (jsonError) {
          console.error('[UPDATE] Could not parse error response');
        }
        toast({ title: "Erreur", description: errorMessage, variant: "destructive" });
      }
    } catch (error) {
      console.error('[UPDATE] Exception caught:', error);
      toast({ title: "Erreur", description: "Erreur lors de la mise à jour du service", variant: "destructive" });
    }
  };

  const handleUpdateOption = async (e) => {
    e.preventDefault();
    if (!editingOption) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/options/${editingOption.key}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editingOption.name,
          description: editingOption.description,
          price: parseFloat(editingOption.price),
          compatible_services: editingOption.compatible_services,
          active: editingOption.active
        })
      });

      if (response.ok) {
        toast({ title: "Succès", description: "Option mise à jour avec succès" });
        setEditingOption(null);
        loadOptions();
      } else {
        const error = await response.json();
        toast({ title: "Erreur", description: error.detail || "Erreur lors de la mise à jour", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Erreur lors de la mise à jour de l'option", variant: "destructive" });
    }
  };

  const handleDeleteService = async (serviceKey) => {
    if (!window.confirm('⚠️ ATTENTION : Êtes-vous sûr de vouloir SUPPRIMER DÉFINITIVEMENT ce service ?\n\nCette action est IRRÉVERSIBLE. Le service sera complètement effacé de la base de données.')) return;

    console.log('[DELETE] Starting deletion for service:', serviceKey);
    console.log('[DELETE] API URL:', process.env.REACT_APP_BACKEND_URL);
    console.log('[DELETE] Token:', localStorage.getItem('token') ? 'Present' : 'Missing');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/services/${serviceKey}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      console.log('[DELETE] Response status:', response.status);
      console.log('[DELETE] Response ok:', response.ok);
      console.log('[DELETE] Response content-type:', response.headers.get('content-type'));
      
      if (response.ok) {
        // Check if response has JSON body
        const contentType = response.headers.get('content-type');
        let result = null;
        
        if (contentType && contentType.includes('application/json')) {
          try {
            result = await response.json();
            console.log('[DELETE] Success response:', result);
          } catch (jsonError) {
            console.warn('[DELETE] Could not parse JSON response, but deletion succeeded');
          }
        }
        
        toast({ title: "Succès", description: "Service supprimé définitivement" });
        
        // Force reload services
        console.log('[DELETE] Reloading services list...');
        await loadServices();
        console.log('[DELETE] Services reloaded');
      } else {
        let errorMessage = "Erreur lors de la suppression du service";
        let errorDetail = null;
        
        try {
          const error = await response.json();
          errorDetail = error;
          errorMessage = error.detail || errorMessage;
          console.error('[DELETE] Error response:', error);
          console.error('[DELETE] Error detail:', error.detail);
        } catch (jsonError) {
          console.error('[DELETE] Could not parse error response:', jsonError);
          console.error('[DELETE] Response was not JSON');
        }
        
        console.error('[DELETE] FINAL ERROR MESSAGE:', errorMessage);
        console.error('[DELETE] Status code:', response.status);
        
        toast({ 
          title: "Erreur", 
          description: `${errorMessage} (Status: ${response.status})`, 
          variant: "destructive" 
        });
      }
    } catch (error) {
      console.error('[DELETE] Exception caught:', error);
      console.error('[DELETE] Exception name:', error.name);
      console.error('[DELETE] Exception message:', error.message);
      console.error('[DELETE] Exception stack:', error.stack);
      
      toast({ 
        title: "Erreur", 
        description: `${error.message || "Erreur lors de la suppression"}`, 
        variant: "destructive" 
      });
    }
  };

  const handleDeleteOption = async (optionKey) => {
    if (!window.confirm('⚠️ ATTENTION : Êtes-vous sûr de vouloir SUPPRIMER DÉFINITIVEMENT cette option ?\n\nCette action est IRRÉVERSIBLE. L\'option sera complètement effacée de la base de données.')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/options/${optionKey}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        toast({ title: "Succès", description: "Option supprimée définitivement" });
        loadOptions();
      } else {
        const error = await response.json();
        toast({ 
          title: "Erreur", 
          description: error.detail || "Erreur lors de la suppression de l'option", 
          variant: "destructive" 
        });
      }
    } catch (error) {
      console.error('Delete option error:', error);
      toast({ 
        title: "Erreur", 
        description: error.message || "Erreur lors de la suppression", 
        variant: "destructive" 
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#111111]">
      <EngineerSidebar />
      <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
              <Settings className="h-10 w-10 text-[#C00000]" />
              <span className="text-[#C00000]">Administration</span>
            </h1>
            <p className="text-[#A6A6A6]">Gérer les services et options</p>
          </div>

          <Tabs defaultValue="services" className="w-full">
            <TabsList className="bg-[#1a1a1a] mb-6">
              <TabsTrigger value="services" className="data-[state=active]:bg-[#C00000] data-[state=active]:text-white">
                Services Principaux
              </TabsTrigger>
              <TabsTrigger value="options" className="data-[state=active]:bg-[#C00000] data-[state=active]:text-white">
                Options Additionnelles
              </TabsTrigger>
              <TabsTrigger value="consulting" className="data-[state=active]:bg-[#C00000] data-[state=active]:text-white">
                <Stethoscope className="h-4 w-4 mr-2" />
                Consulting
              </TabsTrigger>
            </TabsList>

            {/* SERVICES TAB */}
            <TabsContent value="services">
              {/* Create New Service */}
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20 mb-8">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Plus className="h-5 w-5 text-[#C00000]" />
                    Créer un nouveau service
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreateService} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-[#A6A6A6]">Nom du service</Label>
                        <Input
                          value={newService.name}
                          onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                          className="bg-[#111111] border-[#A6A6A6]/30 text-white"
                          placeholder="Stage 1"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#A6A6A6]">Prix (€ HT)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={newService.price}
                          onChange={(e) => setNewService({ ...newService, price: e.target.value })}
                          className="bg-[#111111] border-[#A6A6A6]/30 text-white"
                          placeholder="95.00"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#A6A6A6]">Description</Label>
                      <Input
                        value={newService.description}
                        onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                        className="bg-[#111111] border-[#A6A6A6]/30 text-white"
                        placeholder="Augmentation de puissance..."
                        required
                      />
                    </div>
                    <Button type="submit" className="bg-[#C00000] hover:bg-[#C00000]/90 text-white">
                      <Plus className="h-4 w-4 mr-2" />
                      Créer le service
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Services List */}
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                <CardHeader>
                  <CardTitle className="text-white">Services existants ({services.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <p className="text-[#A6A6A6] text-center py-8">Chargement...</p>
                  ) : services.length === 0 ? (
                    <p className="text-[#A6A6A6] text-center py-8">Aucun service créé</p>
                  ) : (
                    <div className="space-y-3">
                      {services.map((service) => (
                        <div key={service.key} className="bg-[#0a0a0a] p-4 rounded-lg">
                          {editingService?.key === service.key ? (
                            <form onSubmit={handleUpdateService} className="space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Input
                                  value={editingService.name}
                                  onChange={(e) => setEditingService({ ...editingService, name: e.target.value })}
                                  className="bg-[#111111] border-[#A6A6A6]/30 text-white"
                                  placeholder="Nom du service"
                                  required
                                />
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={editingService.price}
                                  onChange={(e) => setEditingService({ ...editingService, price: e.target.value })}
                                  className="bg-[#111111] border-[#A6A6A6]/30 text-white"
                                  placeholder="Prix"
                                  required
                                />
                              </div>
                              <Input
                                value={editingService.description}
                                onChange={(e) => setEditingService({ ...editingService, description: e.target.value })}
                                className="bg-[#111111] border-[#A6A6A6]/30 text-white"
                                placeholder="Description"
                                required
                              />
                              <div className="flex gap-2">
                                <Button type="submit" size="sm" className="bg-[#C00000] hover:bg-[#C00000]/90">
                                  Enregistrer
                                </Button>
                                <Button 
                                  type="button" 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => setEditingService(null)}
                                  className="border-[#A6A6A6] text-white"
                                >
                                  Annuler
                                </Button>
                              </div>
                            </form>
                          ) : (
                            <div className="flex justify-between items-center">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <h3 className="text-white font-bold">{service.name}</h3>
                                  {!service.active && (
                                    <span className="text-xs bg-gray-500/20 text-gray-400 px-2 py-1 rounded">Désactivé</span>
                                  )}
                                </div>
                                <p className="text-[#A6A6A6] text-sm">{service.description}</p>
                              </div>
                              <div className="flex items-center gap-4">
                                <p className="text-2xl font-bold text-[#C00000]">{service.price}€ HT</p>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setEditingService({ ...service })}
                                    className="border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDeleteService(service.key || service.id)}
                                    className="border-red-500 text-red-500 hover:bg-red-500/10"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* OPTIONS TAB */}
            <TabsContent value="options">
              {/* Create New Option */}
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20 mb-8">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Plus className="h-5 w-5 text-[#C00000]" />
                    Créer une nouvelle option
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreateOption} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-[#A6A6A6]">Nom de l'option</Label>
                        <Input
                          value={newOption.name}
                          onChange={(e) => setNewOption({ ...newOption, name: e.target.value })}
                          className="bg-[#111111] border-[#A6A6A6]/30 text-white"
                          placeholder="Pop & Bang"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#A6A6A6]">Prix (€ HT)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={newOption.price}
                          onChange={(e) => setNewOption({ ...newOption, price: e.target.value })}
                          className="bg-[#111111] border-[#A6A6A6]/30 text-white"
                          placeholder="50.00"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#A6A6A6]">Description</Label>
                      <Input
                        value={newOption.description}
                        onChange={(e) => setNewOption({ ...newOption, description: e.target.value })}
                        className="bg-[#111111] border-[#A6A6A6]/30 text-white"
                        placeholder="Effet sonore pop and bang..."
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#A6A6A6]">Catégorie</Label>
                      <select
                        value={newOption.category}
                        onChange={(e) => setNewOption({ ...newOption, category: e.target.value })}
                        className="w-full px-3 py-2 bg-[#111111] border border-[#A6A6A6]/30 text-white rounded-md"
                      >
                        <option value="performance">Performance</option>
                        <option value="eco">Économie</option>
                        <option value="diagnostic">Diagnostic</option>
                      </select>
                    </div>
                    <Button type="submit" className="bg-[#C00000] hover:bg-[#C00000]/90 text-white">
                      <Plus className="h-4 w-4 mr-2" />
                      Créer l'option
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Options List */}
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                <CardHeader>
                  <CardTitle className="text-white">Options existantes ({options.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <p className="text-[#A6A6A6] text-center py-8">Chargement...</p>
                  ) : options.length === 0 ? (
                    <p className="text-[#A6A6A6] text-center py-8">Aucune option créée</p>
                  ) : (
                    <div className="space-y-3">
                      {options.map((option) => (
                        <div key={option.key} className="bg-[#0a0a0a] p-4 rounded-lg">
                          {editingOption?.key === option.key ? (
                            <form onSubmit={handleUpdateOption} className="space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Input
                                  value={editingOption.name}
                                  onChange={(e) => setEditingOption({ ...editingOption, name: e.target.value })}
                                  className="bg-[#111111] border-[#A6A6A6]/30 text-white"
                                  placeholder="Nom de l'option"
                                  required
                                />
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={editingOption.price}
                                  onChange={(e) => setEditingOption({ ...editingOption, price: e.target.value })}
                                  className="bg-[#111111] border-[#A6A6A6]/30 text-white"
                                  placeholder="Prix"
                                  required
                                />
                              </div>
                              <Input
                                value={editingOption.description}
                                onChange={(e) => setEditingOption({ ...editingOption, description: e.target.value })}
                                className="bg-[#111111] border-[#A6A6A6]/30 text-white"
                                placeholder="Description"
                                required
                              />
                              <div className="flex gap-2">
                                <Button type="submit" size="sm" className="bg-[#C00000] hover:bg-[#C00000]/90">
                                  Enregistrer
                                </Button>
                                <Button 
                                  type="button" 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => setEditingOption(null)}
                                  className="border-[#A6A6A6] text-white"
                                >
                                  Annuler
                                </Button>
                              </div>
                            </form>
                          ) : (
                            <div className="flex justify-between items-center">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <h3 className="text-white font-bold">{option.name}</h3>
                                  {!option.active && (
                                    <span className="text-xs bg-gray-500/20 text-gray-400 px-2 py-1 rounded">Désactivé</span>
                                  )}
                                </div>
                                <p className="text-[#A6A6A6] text-sm">{option.description}</p>
                                <span className="text-xs bg-[#C00000]/20 text-[#C00000] px-2 py-1 rounded mt-2 inline-block">
                                  {option.category}
                                </span>
                              </div>
                              <div className="flex items-center gap-4">
                                <p className="text-2xl font-bold text-[#C00000]">{option.price}€ HT</p>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setEditingOption({ ...option })}
                                    className="border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDeleteOption(option.key)}
                                    className="border-red-500 text-red-500 hover:bg-red-500/10"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* CONSULTING TAB */}
            <TabsContent value="consulting">
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Stethoscope className="h-6 w-6 mr-2 text-[#C00000]" />
                    Paramètres Consulting Diagnostic
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="bg-[#111111] p-6 rounded-lg border border-[#A6A6A6]/20">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-white font-semibold mb-1">Prix du Consulting</h3>
                          <p className="text-[#A6A6A6] text-sm">
                            Tarif appliqué pour chaque demande de consulting diagnostic
                          </p>
                        </div>
                        {!editingConsultingPrice && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#C00000] text-[#C00000] hover:bg-[#C00000]/10"
                            onClick={() => setEditingConsultingPrice(true)}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Modifier
                          </Button>
                        )}
                      </div>

                      {editingConsultingPrice ? (
                        <div className="flex items-end gap-4">
                          <div className="flex-1">
                            <Label htmlFor="consulting-price" className="text-[#A6A6A6]">Prix (€)</Label>
                            <Input
                              id="consulting-price"
                              type="number"
                              step="0.01"
                              value={consultingPrice}
                              onChange={(e) => setConsultingPrice(parseFloat(e.target.value))}
                              className="bg-[#111111] border-[#A6A6A6]/30 text-white"
                            />
                          </div>
                          <Button
                            className="bg-[#C00000] hover:bg-[#C00000]/90"
                            onClick={handleUpdateConsultingPrice}
                          >
                            Enregistrer
                          </Button>
                          <Button
                            variant="outline"
                            className="border-[#A6A6A6] text-white"
                            onClick={() => {
                              setEditingConsultingPrice(false);
                              loadConsultingPrice();
                            }}
                          >
                            Annuler
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <div className="text-4xl font-bold text-[#C00000]">
                            {consultingPrice}€
                          </div>
                          <span className="text-[#A6A6A6]">par consulting</span>
                        </div>
                      )}
                    </div>

                    <div className="bg-[#C00000]/10 border border-[#C00000]/30 rounded-lg p-4">
                      <p className="text-[#A6A6A6] text-sm">
                        💡 <strong className="text-white">Info :</strong> Ce tarif sera appliqué automatiquement 
                        à toutes les nouvelles demandes de consulting diagnostic. Les demandes existantes 
                        conservent leur prix d'origine.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
